import { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Trash2, Edit2, FolderOpen, Upload, X, Save, ChevronDown, ChevronUp, Image as ImageIcon, FileUp } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { fileToCompressedDataUrl } from '../../lib/imageCompress';
import { BulkImportDialog } from './BulkImportDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function errMsg(e, fallback) {
  const detail = e?.response?.data?.detail;
  if (Array.isArray(detail)) return detail.map(d => d.msg || JSON.stringify(d)).join('; ');
  if (typeof detail === 'string') return detail;
  if (e?.response?.status) return `${fallback} (${e.response.status})`;
  return e?.message || fallback;
}

function CategoryDialog({ open, mode, data, onClose, onSave }) {
  const [name, setName] = useState(data?.name || '');
  const [description, setDescription] = useState(data?.description || '');
  const [image, setImage] = useState(data?.image || '');
  const [processingImg, setProcessingImg] = useState(false);

  const handleImg = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error('Image is too large. Please choose a file under 10MB.');
      return;
    }
    setProcessingImg(true);
    try {
      const compressed = await fileToCompressedDataUrl(f);
      setImage(compressed);
    } catch (_err) {
      toast.error('Could not process image. Try a different file.');
    } finally {
      setProcessingImg(false);
    }
  };

  const handleSave = () => {
    if (!name.trim()) { toast.error('Name is required'); return; }
    if (processingImg) { toast.error('Image is still processing…'); return; }
    onSave({ name, description, image });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'Add Category' : 'Edit Category'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <Label>Name *</Label>
            <Input data-testid="cat-name-input" value={name} onChange={e => setName(e.target.value)} className="mt-1" />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea data-testid="cat-desc-input" value={description} onChange={e => setDescription(e.target.value)} className="mt-1" rows={3} />
          </div>
          <div>
            <Label>Image</Label>
            <div className="mt-1">
              {image ? (
                <div className="relative">
                  <img data-testid="cat-image-preview" src={image} alt="" className="w-full h-32 object-cover rounded-lg" />
                  <button onClick={() => setImage('')} className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <label className="flex items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer hover:border-slate-400">
                  <div className="text-center">
                    <Upload className="w-8 h-8 text-slate-400 mx-auto mb-1" />
                    <span className="text-sm text-slate-500">Upload</span>
                  </div>
                  <input data-testid="cat-image-input" type="file" accept="image/*" className="hidden" onChange={handleImg} />
                </label>
              )}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="cat-save-button" onClick={handleSave} disabled={processingImg} className="bg-[#01195d]">
            {processingImg
              ? <><span className="w-4 h-4 mr-2 border-2 border-white/60 border-t-white rounded-full animate-spin" />Processing…</>
              : <><Save className="w-4 h-4 mr-2" />Save</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProductDialog({ open, mode, data, onClose, onSave }) {
  const [name, setName] = useState(data?.name || '');
  const [description, setDescription] = useState(data?.description || '');
  const [image, setImage] = useState(data?.image || '');
  const [cartonSize, setCartonSize] = useState(data?.carton_size || '');
  const [unitSize, setUnitSize] = useState(data?.unit_size || '');
  const [ingredients, setIngredients] = useState(data?.ingredients || '');
  const [countryOfOrigin, setCountryOfOrigin] = useState(data?.country_of_origin || '');
  const [sku, setSku] = useState(data?.sku || '');
  const [wholesalePrice, setWholesalePrice] = useState(data?.wholesale_price != null ? String(data.wholesale_price) : '');
  const [stockStatus, setStockStatus] = useState(data?.stock_status || '');
  const [weight, setWeight] = useState(data?.weight || '');
  const [dimensions, setDimensions] = useState(data?.dimensions || '');
  const [brand, setBrand] = useState(data?.brand || '');
  const [barcode, setBarcode] = useState(data?.barcode || '');
  const [isFeatured, setIsFeatured] = useState(!!data?.is_featured);
  const [processingImg, setProcessingImg] = useState(false);

  const handleImg = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error('Image is too large. Please choose a file under 10MB.');
      return;
    }
    setProcessingImg(true);
    try {
      const compressed = await fileToCompressedDataUrl(f);
      setImage(compressed);
    } catch (_err) {
      toast.error('Could not process image. Try a different file.');
    } finally {
      setProcessingImg(false);
    }
  };

  const handleSave = () => {
    if (!name.trim()) { toast.error('Name is required'); return; }
    if (processingImg) { toast.error('Image is still processing…'); return; }
    const price = wholesalePrice.trim() === '' ? null : Number(wholesalePrice);
    if (price !== null && Number.isNaN(price)) { toast.error('Wholesale price must be a number'); return; }
    onSave({
      name,
      description,
      image,
      carton_size: cartonSize,
      unit_size: unitSize,
      ingredients,
      country_of_origin: countryOfOrigin,
      sku,
      wholesale_price: price,
      stock_status: stockStatus || null,
      weight,
      dimensions,
      brand,
      barcode,
      is_featured: isFeatured,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'Add Product' : 'Edit Product'}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 py-4">
          <div className="col-span-2">
            <Label>Name *</Label>
            <Input data-testid="prod-name-input" value={name} onChange={e => setName(e.target.value)} className="mt-1" />
          </div>
          <div>
            <Label>SKU / Product Code</Label>
            <Input data-testid="prod-sku-input" value={sku} onChange={e => setSku(e.target.value)} className="mt-1" placeholder="e.g., SKU-001" />
          </div>
          <div>
            <Label>Brand</Label>
            <Input data-testid="prod-brand-input" value={brand} onChange={e => setBrand(e.target.value)} className="mt-1" placeholder="e.g., India Gate" />
          </div>
          <div>
            <Label>Wholesale Price (AUD)</Label>
            <Input data-testid="prod-price-input" type="number" step="0.01" min="0" value={wholesalePrice} onChange={e => setWholesalePrice(e.target.value)} className="mt-1" placeholder="0.00" />
          </div>
          <div>
            <Label>Stock Status</Label>
            <select
              data-testid="prod-stock-select"
              value={stockStatus}
              onChange={e => setStockStatus(e.target.value)}
              className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
            >
              <option value="">— Not set —</option>
              <option value="in_stock">In stock</option>
              <option value="low_stock">Low stock</option>
              <option value="out_of_stock">Out of stock</option>
            </select>
          </div>
          <div>
            <Label>Carton Size</Label>
            <Input data-testid="prod-carton-input" value={cartonSize} onChange={e => setCartonSize(e.target.value)} className="mt-1" placeholder="e.g., 12 x 1kg" />
          </div>
          <div>
            <Label>Unit Size</Label>
            <Input data-testid="prod-unit-input" value={unitSize} onChange={e => setUnitSize(e.target.value)} className="mt-1" placeholder="e.g., 1kg" />
          </div>
          <div>
            <Label>Weight</Label>
            <Input data-testid="prod-weight-input" value={weight} onChange={e => setWeight(e.target.value)} className="mt-1" placeholder="e.g., 5kg" />
          </div>
          <div>
            <Label>Dimensions</Label>
            <Input data-testid="prod-dimensions-input" value={dimensions} onChange={e => setDimensions(e.target.value)} className="mt-1" placeholder="e.g., 40x30x15cm" />
          </div>
          <div>
            <Label>Barcode / GTIN</Label>
            <Input data-testid="prod-barcode-input" value={barcode} onChange={e => setBarcode(e.target.value)} className="mt-1" placeholder="e.g., 8901234567890" />
          </div>
          <div>
            <Label>Country of Origin</Label>
            <Input data-testid="prod-country-input" value={countryOfOrigin} onChange={e => setCountryOfOrigin(e.target.value)} className="mt-1" />
          </div>
          <div className="col-span-2">
            <Label>Description</Label>
            <Textarea value={description} onChange={e => setDescription(e.target.value)} className="mt-1" rows={3} />
          </div>
          <div className="col-span-2">
            <Label>Ingredients</Label>
            <Textarea value={ingredients} onChange={e => setIngredients(e.target.value)} className="mt-1" rows={2} />
          </div>
          <div className="col-span-2 flex items-center gap-2 py-1">
            <input
              data-testid="prod-featured-toggle"
              id="prod-featured"
              type="checkbox"
              checked={isFeatured}
              onChange={e => setIsFeatured(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-[#01195d] focus:ring-[#01195d]"
            />
            <Label htmlFor="prod-featured" className="cursor-pointer">Feature this product (highlighted on homepage)</Label>
          </div>
          <div className="col-span-2">
            <Label>Image</Label>
            <div className="mt-1">
              {image ? (
                <div className="relative">
                  <img data-testid="prod-image-preview" src={image} alt="" className="w-full h-48 object-cover rounded-lg" />
                  <button onClick={() => setImage('')} className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <label className="flex items-center justify-center w-full h-48 border-2 border-dashed rounded-lg cursor-pointer hover:border-slate-400">
                  <div className="text-center">
                    <Upload className="w-10 h-10 text-slate-400 mx-auto mb-2" />
                    <span className="text-sm text-slate-500">Upload</span>
                  </div>
                  <input data-testid="prod-image-input" type="file" accept="image/*" className="hidden" onChange={handleImg} />
                </label>
              )}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="prod-save-button" onClick={handleSave} disabled={processingImg} className="bg-[#01195d]">
            {processingImg
              ? <><span className="w-4 h-4 mr-2 border-2 border-white/60 border-t-white rounded-full animate-spin" />Processing…</>
              : <><Save className="w-4 h-4 mr-2" />Save</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteDialog({ open, type, name, onClose, onConfirm }) {
  return (
    <AlertDialog open={open} onOpenChange={onClose}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete {type === 'cat' ? 'Category' : 'Product'}</AlertDialogTitle>
          <AlertDialogDescription>
            Delete "{name}"? {type === 'cat' && 'All products will also be deleted.'}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="cancel-delete-button">Cancel</AlertDialogCancel>
          <AlertDialogAction data-testid="confirm-delete-button" onClick={onConfirm} className="bg-red-500 hover:bg-red-600">Delete</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function CategoryItem({ category, expanded, onToggle, onEdit, onDelete, products, onAddProduct, onEditProduct, onDeleteProduct }) {
  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid={`category-item-${category.id}`}>
      <div 
        className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50" 
        onClick={onToggle}
        data-testid={`category-header-${category.id}`}
      >
        <div className="flex items-center gap-4">
          {category.image ? (
            <img src={category.image} alt="" className="w-14 h-14 rounded-lg object-cover" />
          ) : (
            <div className="w-14 h-14 bg-slate-100 rounded-lg flex items-center justify-center">
              <ImageIcon className="w-6 h-6 text-slate-400" />
            </div>
          )}
          <div>
            <h3 className="font-semibold text-slate-800" data-testid={`category-name-${category.id}`}>{category.name}</h3>
            <p className="text-sm text-slate-500">{category.product_count} products</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid={`edit-category-${category.id}`} size="sm" variant="ghost" onClick={e => { e.stopPropagation(); onEdit(); }}>
            <Edit2 className="w-4 h-4" />
          </Button>
          <Button data-testid={`delete-category-${category.id}`} size="sm" variant="ghost" className="text-red-500" onClick={e => { e.stopPropagation(); onDelete(); }}>
            <Trash2 className="w-4 h-4" />
          </Button>
          {expanded ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
        </div>
      </div>
      {expanded && (
        <div className="border-t bg-slate-50 p-4">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-medium text-slate-700">Products</h4>
            <Button data-testid={`add-product-${category.id}`} size="sm" onClick={onAddProduct} className="bg-[#01195d]">
              <Plus className="w-4 h-4 mr-1" />Add
            </Button>
          </div>
          {!products ? (
            <div className="text-center py-4 text-slate-500">Loading...</div>
          ) : products.length === 0 ? (
            <div className="text-center py-8 text-slate-500">No products yet.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {products.map(p => (
                <ProductCard key={p.id} product={p} onEdit={() => onEditProduct(p)} onDelete={() => onDeleteProduct(p)} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ProductCard({ product, onEdit, onDelete }) {
  const stockLabel = {
    in_stock: { text: 'In stock', cls: 'bg-green-100 text-green-700' },
    low_stock: { text: 'Low stock', cls: 'bg-amber-100 text-amber-700' },
    out_of_stock: { text: 'Out of stock', cls: 'bg-red-100 text-red-700' },
  }[product.stock_status];
  return (
    <div className="bg-white rounded-lg p-4 shadow-sm relative" data-testid={`product-card-${product.id}`}>
      {product.is_featured && (
        <span data-testid={`product-featured-${product.id}`} className="absolute top-2 left-2 bg-amber-400 text-amber-900 text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wide">Featured</span>
      )}
      {product.image ? (
        <img data-testid={`product-image-${product.id}`} src={product.image} alt="" className="w-full h-32 rounded-lg object-cover mb-3" />
      ) : (
        <div className="w-full h-32 bg-slate-100 rounded-lg flex items-center justify-center mb-3">
          <ImageIcon className="w-10 h-10 text-slate-300" />
        </div>
      )}
      <h5 className="font-semibold text-slate-800 mb-0.5" data-testid={`product-name-${product.id}`}>{product.name}</h5>
      <div className="flex items-center flex-wrap gap-x-2 gap-y-1 mb-2">
        {product.sku && <span className="text-[11px] text-slate-500 font-mono">{product.sku}</span>}
        {product.brand && <span className="text-[11px] text-slate-500">· {product.brand}</span>}
      </div>
      <div className="flex items-center flex-wrap gap-2 mb-3">
        {product.wholesale_price != null && (
          <span className="text-sm font-semibold text-[#01195d]">${Number(product.wholesale_price).toFixed(2)}</span>
        )}
        {stockLabel && (
          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${stockLabel.cls}`}>{stockLabel.text}</span>
        )}
        {product.unit_size && <span className="text-xs text-slate-500">{product.unit_size}</span>}
      </div>
      <div className="flex gap-2">
        <Button data-testid={`edit-product-${product.id}`} size="sm" variant="outline" className="flex-1" onClick={onEdit}>Edit</Button>
        <Button data-testid={`delete-product-${product.id}`} size="sm" variant="outline" className="text-red-500" onClick={onDelete}>
          <Trash2 className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );
}

export function ProductsTab({ creds }) {
  const [cats, setCats] = useState([]);
  const [prods, setProds] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState({ type: null, mode: null, data: null, catId: null });
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  const auth = { auth: creds };

  useEffect(() => {
    loadCats();
  }, []);

  const loadCats = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/categories`);
      setCats(r.data);
    } catch (e) {
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const loadProds = async (cid) => {
    try {
      const r = await axios.get(`${API}/categories/${cid}/products`);
      setProds(p => ({ ...p, [cid]: r.data }));
    } catch (e) {
      toast.error('Failed to load products');
    }
  };

  const toggle = async (cid) => {
    if (expanded === cid) {
      setExpanded(null);
    } else {
      setExpanded(cid);
      if (!prods[cid]) await loadProds(cid);
    }
  };

  const seed = async () => {
    try {
      const r = await axios.post(`${API}/seed-categories`, {}, auth);
      toast.success(r.data.message);
      loadCats();
    } catch (e) {
      toast.error('Failed to initialize');
    }
  };

  const saveCat = async (form) => {
    try {
      if (dialog.mode === 'create') {
        await axios.post(`${API}/categories`, form, auth);
      } else {
        await axios.put(`${API}/categories/${dialog.data.id}`, form, auth);
      }
      toast.success('Saved');
      setDialog({ type: null });
      loadCats();
    } catch (e) {
      toast.error(errMsg(e, 'Failed to save category'));
    }
  };

  const delCat = async (id) => {
    try {
      await axios.delete(`${API}/categories/${id}`, auth);
      toast.success('Deleted');
      setDeleteDialog(null);
      loadCats();
    } catch (e) {
      toast.error(errMsg(e, 'Failed to delete'));
    }
  };

  const saveProd = async (form, cid) => {
    try {
      if (dialog.mode === 'create') {
        await axios.post(`${API}/products`, { ...form, category_id: cid }, auth);
      } else {
        await axios.put(`${API}/products/${dialog.data.id}`, form, auth);
      }
      toast.success('Saved');
      setDialog({ type: null });
      loadProds(cid);
      loadCats();
    } catch (e) {
      toast.error(errMsg(e, 'Failed to save product'));
    }
  };

  const delProd = async (id, cid) => {
    try {
      await axios.delete(`${API}/products/${id}`, auth);
      toast.success('Deleted');
      setDeleteDialog(null);
      loadProds(cid);
      loadCats();
    } catch (e) {
      toast.error(errMsg(e, 'Failed to delete'));
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-slate-800">Product Categories</h2>
        <div className="flex gap-3">
          {cats.length === 0 && (
            <Button onClick={seed} variant="outline">
              <FolderOpen className="w-4 h-4 mr-2" />Initialize
            </Button>
          )}
          <Button
            data-testid="bulk-import-button"
            variant="outline"
            onClick={() => setBulkOpen(true)}
            disabled={cats.length === 0}
          >
            <FileUp className="w-4 h-4 mr-2" />Bulk Import
          </Button>
          <Button data-testid="add-category-button" onClick={() => setDialog({ type: 'cat', mode: 'create' })} className="bg-[#01195d]">
            <Plus className="w-4 h-4 mr-2" />Add Category
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-500">Loading...</div>
      ) : cats.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center">
          <FolderOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-700 mb-2">No Categories</h3>
          <p className="text-slate-500">Click Initialize to get started.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {cats.map(c => (
            <CategoryItem
              key={c.id}
              category={c}
              expanded={expanded === c.id}
              onToggle={() => toggle(c.id)}
              onEdit={() => setDialog({ type: 'cat', mode: 'edit', data: c })}
              onDelete={() => setDeleteDialog({ type: 'cat', id: c.id, name: c.name })}
              products={prods[c.id]}
              onAddProduct={() => setDialog({ type: 'prod', mode: 'create', catId: c.id })}
              onEditProduct={(p) => setDialog({ type: 'prod', mode: 'edit', data: p, catId: c.id })}
              onDeleteProduct={(p) => setDeleteDialog({ type: 'prod', id: p.id, name: p.name, catId: c.id })}
            />
          ))}
        </div>
      )}

      {dialog.type === 'cat' && (
        <CategoryDialog
          open={true}
          mode={dialog.mode}
          data={dialog.data}
          onClose={() => setDialog({ type: null })}
          onSave={saveCat}
        />
      )}
      
      {dialog.type === 'prod' && (
        <ProductDialog
          open={true}
          mode={dialog.mode}
          data={dialog.data}
          onClose={() => setDialog({ type: null })}
          onSave={(f) => saveProd(f, dialog.catId)}
        />
      )}
      
      {deleteDialog && (
        <DeleteDialog
          open={true}
          type={deleteDialog.type}
          name={deleteDialog.name}
          onClose={() => setDeleteDialog(null)}
          onConfirm={() => deleteDialog.type === 'cat' ? delCat(deleteDialog.id) : delProd(deleteDialog.id, deleteDialog.catId)}
        />
      )}

      {bulkOpen && (
        <BulkImportDialog
          open={true}
          onClose={() => setBulkOpen(false)}
          creds={creds}
          onImported={() => {
            loadCats();
            if (expanded) loadProds(expanded);
          }}
        />
      )}
    </div>
  );
}

export default ProductsTab;
